import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetEmailMarketingConfig,
  GetAudienceCount,
  GetCampaigns,
  GetCampaign,
  GetCampaignRecipients,
  CreateCampaign,
  StartCampaign,
  RunCampaignNow,
  DeleteCampaign,
} from "Services/API/EmailMarketing.service";

// React-query hooks for the Emails (marketing) page — same style as
// CustomHooks/useOperations. No auto-polling; the page drives refetch on action.

export const useEmailMarketingConfig = () =>
  useQuery(["mktConfig"], GetEmailMarketingConfig, { refetchInterval: false });

// Live audience count for the create modal (paid/non-refunded within windowDays).
export const useAudienceCount = (windowDays) =>
  useQuery(["mktAudience", windowDays], () => GetAudienceCount({ windowDays }), {
    refetchInterval: false,
    keepPreviousData: true,
    enabled: !!windowDays,
  });

// refetchOnMount "always" + refetchOnWindowFocus → fresh data every time you land on the
// listing, open a campaign, or switch tabs (the tab content remounts), so webhook-driven
// delivery/open/click counts are never stale. staleTime 0 keeps it honest.
export const useCampaigns = () =>
  useQuery(["mktCampaigns"], GetCampaigns, {
    refetchInterval: false, keepPreviousData: true, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true,
  });

export const useCampaign = (id) =>
  useQuery(["mktCampaign", id], () => GetCampaign(id), {
    enabled: !!id, refetchInterval: false, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true,
  });

export const useCampaignRecipients = (id, page, perPage) =>
  useQuery(
    ["mktRecipients", id, page, perPage],
    () => GetCampaignRecipients(id, { page, perPage }),
    { enabled: !!id, keepPreviousData: true, refetchInterval: false, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true }
  );

const invalidateCampaigns = (qc) => () => qc.invalidateQueries(["mktCampaigns"]);

export const useCreateCampaign = () => {
  const qc = useQueryClient();
  return useMutation(CreateCampaign, { onSuccess: invalidateCampaigns(qc) });
};

export const useStartCampaign = () => {
  const qc = useQueryClient();
  return useMutation(StartCampaign, { onSuccess: invalidateCampaigns(qc) });
};

export const useRunCampaignNow = () => {
  const qc = useQueryClient();
  return useMutation(RunCampaignNow, { onSuccess: invalidateCampaigns(qc) });
};

export const useDeleteCampaign = () => {
  const qc = useQueryClient();
  return useMutation(DeleteCampaign, { onSuccess: invalidateCampaigns(qc) });
};
